/**
 * React Query configuration for the doctor platform
 * 
 * This module provides QueryClient configuration optimized for an online-first
 * medical application with proper error handling, caching strategies, and
 * mutation defaults with optimistic updates.
 */

import { MutationCache, QueryCache, QueryClient, type DefaultOptions } from '@tanstack/react-query';
import { 
  classifyError, 
  logError, 
  showErrorToast,
  showSuccessToast,
  getReactQueryRetryFn,
  getReactQueryRetryDelay,
  DEFAULT_RETRY_CONFIGS,
  isAuthError,
  isNetworkError
} from '@/lib/error-handling';

/**
 * Default query options optimized for online-first medical app
 */
const queryDefaults: DefaultOptions = {
  queries: {
    // Cache configuration
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes after last use (renamed from cacheTime in v5)
    
    // Retry configuration using new error handling system
    retry: getReactQueryRetryFn(DEFAULT_RETRY_CONFIGS.api),
    retryDelay: getReactQueryRetryDelay(DEFAULT_RETRY_CONFIGS.api),
    
    // Refetch configuration
    refetchOnWindowFocus: true, // Refetch when user returns to app
    refetchOnReconnect: true, // Refetch when network reconnects
    refetchOnMount: true, // Always refetch on component mount for medical data freshness
    
    // Error configuration
    throwOnError: false, // Let components handle errors via error state
    
    // Network mode
    networkMode: 'online', // Only run queries when online for medical data integrity
  },
  mutations: {
    // Retry configuration for mutations using new error handling
    retry: getReactQueryRetryFn({
      ...DEFAULT_RETRY_CONFIGS.api,
      maxAttempts: 2, // Be more conservative with mutations
      retryCondition: (error, attemptCount) => {
        const classified = classifyError(error);
        // Only retry network errors and server errors for mutations
        return classified.retryable && (classified.type === 'network' || classified.type === 'server') && attemptCount < 2;
      }
    }),
    retryDelay: getReactQueryRetryDelay({
      ...DEFAULT_RETRY_CONFIGS.api,
      baseDelay: 1000,
      maxDelay: 5000
    }),
    
    // Network mode
    networkMode: 'online', // Only run mutations when online
    
    // Error configuration
    throwOnError: false, // Let components handle errors via error state
  },
};

/**
 * Query cache configuration with global error handling
 */
const createQueryCache = (): QueryCache => {
  return new QueryCache({
    onError: (error: any, query) => {
      // Use centralized error logging with context
      logError(error, {
        queryKey: query.queryKey,
        component: 'QueryCache',
        action: 'query',
        timestamp: new Date().toISOString()
      });
      
      const classified = classifyError(error);
      
      // Handle authentication errors globally
      if (classified.type === 'auth') {
        console.warn('Authentication error detected, user should be redirected to login');
        // Auth state will be handled by auth provider, but we can show a toast
        showErrorToast(error, 'load data');
      }
      
      // Handle network errors - don't show toast as network monitor will handle this
      if (classified.type === 'network') {
        console.warn('Network error detected:', error.message);
      }
      
      // For other errors, only show toast if it's a user-facing query
      if (classified.type !== 'network' && classified.type !== 'auth') {
        // Only show error toasts for interactive queries (not background refetches)
        const isBackgroundRefetch = query.state.data !== undefined;
        if (!isBackgroundRefetch) {
          showErrorToast(error, 'load data');
        }
      }
    },
    onSuccess: (data, query) => {
      // Optional: Log successful queries in development
      if (import.meta.env.DEV) {
        console.log(`Query success for ${query.queryKey}`);
      }
    },
  });
};

/**
 * Mutation cache configuration with global error and success handling
 */
const createMutationCache = (): MutationCache => {
  return new MutationCache({
    onError: (error: any, variables, _context, mutation) => {
      // Use centralized error logging with context
      logError(error, {
        mutationKey: mutation.options.mutationKey,
        variables: import.meta.env.DEV ? variables : undefined,
        component: 'MutationCache',
        action: 'mutation',
        timestamp: new Date().toISOString()
      });
      
      const classified = classifyError(error);
      
      // Global mutation error handling - show toast unless handled by component
      switch (classified.type) {
        case 'auth':
          console.warn('Authentication error in mutation, user should be redirected to login');
          showErrorToast(error, 'save changes');
          break;
          
        case 'validation':
          console.warn('Validation error in mutation:', error.message);
          // Let individual mutations handle validation errors with specific messages
          break;
          
        case 'permission':
          console.warn('Permission error in mutation:', error.message);
          showErrorToast(error, 'perform this action');
          break;
          
        case 'network':
          console.warn('Network error in mutation:', error.message);
          // Network monitor will handle this
          break;
          
        case 'rate_limit':
          console.warn('Rate limit error in mutation:', error.message);
          showErrorToast(error, 'complete request');
          break;
          
        default:
          console.warn('Server error in mutation:', error.message);
          showErrorToast(error, 'save changes');
      }
    },
    onSuccess: (data, variables, _context, mutation) => {
      // Log successful mutations in development
      if (import.meta.env.DEV) {
        console.log('Mutation success:', mutation.options.mutationKey);
      }
      
      // Show success toast for mutations that create/update/delete data
      const mutationKey = mutation.options.mutationKey;
      if (mutationKey && Array.isArray(mutationKey)) {
        const [entity, action] = mutationKey;
        
        // Show success messages for CRUD operations
        if (action === 'create') {
          showSuccessToast(`${entity} created successfully`);
        } else if (action === 'update') {
          showSuccessToast(`${entity} updated successfully`);
        } else if (action === 'delete') {
          showSuccessToast(`${entity} deleted successfully`);
        }
      }
    },
  });
};

/**
 * Create configured QueryClient instance
 */
export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: queryDefaults,
    queryCache: createQueryCache(),
    mutationCache: createMutationCache(),
  });
};

/**
 * Default QueryClient instance for the application
 */
export const queryClient = createQueryClient();

/**
 * Utility functions for React Query management
 */
export const queryUtils = {
  /**
   * Invalidate all queries for a specific entity
   */
  invalidateEntity(entityKey: string | string[]) {
    const key = Array.isArray(entityKey) ? entityKey : [entityKey];
    return queryClient.invalidateQueries({ queryKey: key });
  },

  /**
   * Remove all queries for a specific entity
   */
  removeEntity(entityKey: string | string[]) {
    const key = Array.isArray(entityKey) ? entityKey : [entityKey];
    return queryClient.removeQueries({ queryKey: key });
  },

  /**
   * Prefetch data for a query
   */
  prefetch(queryKey: string[], queryFn: () => Promise<any>, staleTime?: number) {
    return queryClient.prefetchQuery({
      queryKey,
      queryFn,
      staleTime: staleTime || 5 * 60 * 1000, // 5 minutes default
    });
  },

  /**
   * Set query data manually (for optimistic updates)
   */
  setQueryData<T>(queryKey: string[], data: T | ((oldData: T | undefined) => T)) {
    return queryClient.setQueryData(queryKey, data);
  },

  /**
   * Get query data from cache
   */
  getQueryData<T>(queryKey: string[]): T | undefined {
    return queryClient.getQueryData(queryKey);
  },

  /**
   * Cancel outgoing queries for a specific key
   */
  cancelQueries(queryKey: string[]) {
    return queryClient.cancelQueries({ queryKey });
  },

  /**
   * Reset all queries and clear cache
   */
  resetQueries() {
    return queryClient.resetQueries();
  },

  /**
   * Clear cache and invalidate all queries
   */
  clear() {
    queryClient.clear();
  },

  /**
   * Get cache stats for debugging
   */
  getCacheStats() {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    
    return {
      totalQueries: queries.length,
      activeQueries: queries.filter(q => q.observers.length > 0).length,
      staleQueries: queries.filter(q => q.isStale()).length,
      fetchingQueries: queries.filter(q => q.isFetching()).length,
    };
  },

  /**
   * Setup automatic cache cleanup for inactive queries
   */
  setupCacheCleanup() {
    // Clean up inactive queries every 5 minutes
    setInterval(() => {
      const cache = queryClient.getQueryCache();
      const queries = cache.getAll();
      const inactiveQueries = queries.filter(
        query => query.observers.length === 0 && 
        query.state.dataUpdatedAt < Date.now() - 10 * 60 * 1000 // 10 minutes old
      );
      
      if (inactiveQueries.length > 0) {
        console.log(`Cleaning up ${inactiveQueries.length} inactive queries`);
        inactiveQueries.forEach(query => {
          cache.remove(query);
        });
      }
    }, 5 * 60 * 1000); // Every 5 minutes
  },

  /**
   * Handle optimistic update pattern
   */
  optimisticUpdate<T>({
    queryKey,
    updaterFn,
    mutationFn,
    onError,
  }: {
    queryKey: string[];
    updaterFn: (oldData: T | undefined) => T;
    mutationFn: () => Promise<T>;
    onError?: (error: any, previousData: T | undefined) => void;
  }) {
    // Store previous data for rollback
    const previousData = this.getQueryData<T>(queryKey);
    
    // Apply optimistic update
    this.setQueryData(queryKey, updaterFn);
    
    // Perform mutation
    return mutationFn().catch((error) => {
      // Rollback on error
      this.setQueryData(queryKey, previousData);
      if (onError) {
        onError(error, previousData);
      }
      throw error;
    });
  },
};

/**
 * Development helpers
 */
export const devTools = {
  /**
   * Log current cache state
   */
  logCacheState() {
    if (import.meta.env.DEV) {
      console.log('Query Cache State:', queryUtils.getCacheStats());
      console.log('Query Cache:', queryClient.getQueryCache().getAll());
    }
  },

  /**
   * Enable query debugging
   */
  enableDebugMode() {
    if (import.meta.env.DEV) {
      (window as any).reactQueryClient = queryClient;
      console.log('React Query debug mode enabled. Access via window.reactQueryClient');
    }
  },
};

// Enable debug mode in development
if (import.meta.env.DEV) {
  devTools.enableDebugMode();
}

export default queryClient;